import { Times } from '../icons.js';

import { isFiltering } from './search.js';

import type { Filter } from './search.js';

/**
 * Поиск и честность фильтра (3.12).
 *
 * Поиск нужен изредка и не занимает основной экран: строка появляется по
 * вызову и убирается (3.12.1). Ищет по заголовку, описанию и пунктам чек-листа
 * (3.12.2) — это правило живёт в `search.ts`, здесь только управление.
 *
 * **Переключателя области у строки больше нет** (3.12.3). Он занимал треть
 * ширины на телефоне всегда, а нужен был в одном случае из десяти — когда в
 * спринте не нашлось. Теперь строка это только строка, а расширить поиск
 * предлагается там, где показан результат: в полосе под ней и после того, как
 * искать уже начали.
 */
export function SearchBar({
  filter,
  onChange,
  onClose,
}: {
  filter: Filter;
  onChange: (filter: Filter) => void;
  onClose: () => void;
}): React.ReactElement {
  return (
    <div className="search">
      {/*
        Поле выглядит полем (3.12.5): белая подложка и рамка, как у быстрой
        записи. Слитая с фоном строка не читается как место, куда печатают.

        `type="text"`, а не `search`: у поискового поля браузер рисует
        собственный крестик очистки — своим цветом, мимо палитры, и только на
        десктопе. На телефоне его нет вовсе, то есть управление получалось
        разным на разных устройствах.
      */}
      <input
        className="search__input"
        type="text"
        aria-label="Поиск по задачам"
        placeholder="Что ищем?"
        autoFocus
        value={filter.query}
        onChange={(event) => onChange({ ...filter, query: event.target.value })}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      />

      <button type="button" className="iconbtn" aria-label="Убрать поиск" onClick={onClose}>
        <Times />
      </button>
    </div>
  );
}

/** «1 задача», «2 задачи», «5 задач»: число читают глазами каждый раз. */
function pluralTasks(count: number): string {
  const tail = count % 100;
  if (tail >= 11 && tail <= 14) return 'задач';

  switch (count % 10) {
    case 1:
      return 'задача';
    case 2:
    case 3:
    case 4:
      return 'задачи';
    default:
      return 'задач';
  }
}

/**
 * Когда фильтр включён, это видно (3.12.4).
 *
 * Полоса отвечает на вопрос «почему так мало» — называет запрос, область и
 * число найденного — и тут же даёт то, что в этот момент нужно: показать
 * задачи во всех спринтах. Действие одно и словами; кнопки «Показать все
 * задачи» нет (решение владельца, 2026-09-08): фильтр по тегу снимается
 * повторным нажатием на тот же тег, поиск — крестиком у строки, и второй
 * способ в полосе только удваивал первые.
 *
 * Кнопка расширения меняется на обратную, а не остаётся нажатой: человек
 * читает её как «что произойдёт», а не как «где я сейчас», — и второе
 * состояние должно называть обратный ход. Она янтарная, как всякий «частый
 * следующий шаг» в продукте (`.menu__forward`): в полосе это единственное
 * действие, и серым его не находили.
 */
export function FilterNotice({
  filter,
  shown,
  scopeIn,
  onChange,
}: {
  filter: Filter;
  shown: number;
  /** Где ищем, целой фразой: «в Неделе 35», «в сентябре», «во Входящих». */
  scopeIn: string;
  onChange: (filter: Filter) => void;
}): React.ReactElement | null {
  if (!isFiltering(filter)) return null;

  const what: string[] = [];
  if (filter.query.trim().length > 0) what.push(`по запросу «${filter.query.trim()}»`);
  if (filter.tag !== undefined) what.push(`по тегу #${filter.tag}`);

  const where = filter.allSprints ? 'во всех спринтах' : scopeIn;

  return (
    // Область подписана: живых областей на экране две — эта и индикатор
    // синхронизации, — и безымянными их не отличить ни в тесте, ни в озвучке.
    <div className="filter" role="status" aria-label="Фильтр списка">
      {/*
        Тире, а не «найдено»: с числительным глагол требует согласования —
        «найдена 1 задача», но «найдено 2 задачи», — и фраза, собранная из
        кусков, ошибалась бы на каждой единице.
      */}
      <p className="filter__what">
        {what.join(' и ')} {where} — <b>{shown}</b> {pluralTasks(shown)}
      </p>

      <div className="filter__actions">
        <button
          type="button"
          className="quickbtn filter__scope"
          onClick={() => onChange({ ...filter, allSprints: !filter.allSprints })}
        >
          {filter.allSprints ? `Показать задачи ${scopeIn}` : 'Показать задачи во всех спринтах'}
        </button>
      </div>
    </div>
  );
}
